import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import { API_CONFIG } from '../../config/api';

const VaultTab = ({ propertyData, onPropertyDataRefresh }) => {
  const { id } = useParams();
  const [vaultData, setVaultData] = useState({
    ndaSignatureLink: '',
    dataroomChecklist: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingChecklist, setSavingChecklist] = useState(false);
  
  // Custom NDA section state
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [ndaFile, setNdaFile] = useState(null);
  const [uploadingNDA, setUploadingNDA] = useState(false);

  // Discrete Users section state
  const [discreteUserSearch, setDiscreteUserSearch] = useState('');
  const [discreteSearchResults, setDiscreteSearchResults] = useState([]);
  const [selectedDiscreteUser, setSelectedDiscreteUser] = useState(null);
  const [discreteSearchLoading, setDiscreteSearchLoading] = useState(false);
  const [submittingDiscreteUser, setSubmittingDiscreteUser] = useState(false);
  
  // Add new state for discrete users list
  const [discreteUsers, setDiscreteUsers] = useState([]);
  const [loadingDiscreteUsers, setLoadingDiscreteUsers] = useState(false);

  // Add new state for NDA register list
  const [ndaRegister, setNdaRegister] = useState([]);
  const [loadingNdaRegister, setLoadingNdaRegister] = useState(false);

  // Add new state for administration access users list
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);

  // Administration Access section state
  const [adminUserSearch, setAdminUserSearch] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);
  const [submittingAdminUser, setSubmittingAdminUser] = useState(false);

  // Debounced search function
  const debounceTimer = React.useRef(null);
  const discreteDebounceTimer = React.useRef(null);
  const adminDebounceTimer = React.useRef(null);

  const handleInputChange = (field, value) => {
    setVaultData({
      ...vaultData,
      [field]: value
    });
  };

  const searchUsers = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      
      const response = await apiService.get(`/users?roleId=5&searchCriteria=${encodeURIComponent(searchTerm)}`);
      
      // Handle response format: { pagination: {...}, roles: [...], users: [...] }
      const users = response?.users || [];
      
      // Limit to maximum 20 results
      setSearchResults(users.slice(0, 20));
      
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUserSearchChange = (value) => {
    setUserSearch(value);
    setSelectedUser(null); // Clear selection when typing
    
    // Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      searchUsers(value);
    }, 500); // 500ms delay after user stops typing
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setUserSearch(`${user.firstName} ${user.lastName} (${user.emailAddress})`);
    setSearchResults([]);
  };

  const handleNDAFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNdaFile(file);
    } else {
      setNdaFile(null);
    }
  };

  const handleSubmitNDALink = async () => {
    try {
      setSaving(true);

      if (!vaultData.ndaSignatureLink.trim()) {
        alert('Please enter an NDA signature link');
        return;
      }

      const requestBody = {
        userId: 4,
        signedDocUrl: vaultData.ndaSignatureLink
      };

      console.log('Submitting NDA link:', requestBody);

      await apiService.put(`/properties/${id}/signed-doc-url`, requestBody);

      if (onPropertyDataRefresh) {
        await onPropertyDataRefresh();
      }

      alert('NDA signature link submitted successfully!');
      
    } catch (err) {
      console.error('Error submitting NDA link:', err);
      alert('Failed to submit NDA link. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitChecklist = async () => {
    try {
      setSavingChecklist(true);

      if (!vaultData.dataroomChecklist) {
        alert('Please select a dataroom checklist option');
        return;
      }

      const requestBody = {
        checklistName: vaultData.dataroomChecklist,
        userId: 4,
        propertyId: parseInt(id)
      };

      console.log('Submitting dataroom checklist:', requestBody);

      await apiService.post(`/property-data-room/build-dataroom`, requestBody);
      
      alert('Dataroom checklist submitted successfully!');
      
    } catch (err) {
      console.error('Error submitting dataroom checklist:', err);
      alert('Failed to submit dataroom checklist. Please try again.');
    } finally {
      setSavingChecklist(false);
    }
  };

  const handleSubmitCustomNDA = async () => {
    try {
      setUploadingNDA(true);

      if (!selectedUser) {
        alert('Please select a user');
        return;
      }

      if (!ndaFile) {
        alert('Please select a file to upload');
        return;
      }

      // Create FormData for file upload - same format as property-data-room
      const formData = new FormData();
      formData.append('adminuserid', '4'); // Hard coded as requested
      formData.append('userid', selectedUser.id.toString());
      formData.append('propertyid', id.toString());
      formData.append('file', ndaFile, ndaFile.name);

      console.log('Submitting custom NDA for user:', selectedUser);
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      await apiService.uploadFile('/ndaregister/upload', formData);
      
      alert('Custom NDA submitted successfully!');
      
      // Reset form
      setSelectedUser(null);
      setUserSearch('');
      setNdaFile(null);
      
      // Refresh NDA register list after successful upload
      fetchNdaRegister();
      
    } catch (err) {
      console.error('Error submitting custom NDA:', err);
      alert('Failed to submit custom NDA. Please try again.');
    } finally {
      setUploadingNDA(false);
    }
  };

  // Discrete Users functions
  const searchDiscreteUsers = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setDiscreteSearchResults([]);
      return;
    }

    try {
      setDiscreteSearchLoading(true);
      
      const response = await apiService.get(`/users?roleId=5&searchCriteria=${encodeURIComponent(searchTerm)}`);
      
      // Handle response format: { pagination: {...}, roles: [...], users: [...] }
      const users = response?.users || [];
      
      // Limit to maximum 20 results
      setDiscreteSearchResults(users.slice(0, 20));
      
    } catch (err) {
      console.error('Error searching discrete users:', err);
      setDiscreteSearchResults([]);
    } finally {
      setDiscreteSearchLoading(false);
    }
  };

  const handleDiscreteUserSearchChange = (value) => {
    setDiscreteUserSearch(value);
    setSelectedDiscreteUser(null); // Clear selection when typing
    
    // Clear existing timer
    if (discreteDebounceTimer.current) {
      clearTimeout(discreteDebounceTimer.current);
    }
    
    // Set new timer for debounced search
    discreteDebounceTimer.current = setTimeout(() => {
      searchDiscreteUsers(value);
    }, 500); // 500ms delay after user stops typing
  };

  const handleDiscreteUserSelect = (user) => {
    setSelectedDiscreteUser(user);
    setDiscreteUserSearch(`${user.firstName} ${user.lastName} (${user.emailAddress})`);
    setDiscreteSearchResults([]);
  };

  const handleSubmitDiscreteUser = async () => {
    try {
      setSubmittingDiscreteUser(true);

      if (!selectedDiscreteUser) {
        alert('Please select a user');
        return;
      }

      const requestBody = {
        userId: 3,
        propertyId: parseInt(id),
        buyerId: selectedDiscreteUser.id
      };

      console.log('Submitting discrete user:', requestBody);

      await apiService.post(`/property-discrete-users`, requestBody);
      
      alert('Discrete user access granted successfully!');
      
      // Reset form
      setSelectedDiscreteUser(null);
      setDiscreteUserSearch('');
      fetchDiscreteUsers(); // refresh list
      
    } catch (err) {
      console.error('Error submitting discrete user:', err);
      alert('Failed to grant discrete user access. Please try again.');
    } finally {
      setSubmittingDiscreteUser(false);
    }
  };

  // Add useEffect to fetch discrete users when component mounts
  useEffect(() => {
    if (id) {
      fetchDiscreteUsers();
      fetchNdaRegister(); // Add this line
      fetchAdminUsers(); // Add this line
    }
  }, [id]);

  // Add function to fetch discrete users
  const fetchDiscreteUsers = async () => {
    try {
      setLoadingDiscreteUsers(true);
      
      const data = await apiService.get(`/property-discrete-users?propertyId=${id}`);
      setDiscreteUsers(data);
      console.log('Discrete users loaded:', data);
      
    } catch (err) {
      console.error('Error fetching discrete users:', err);
      setDiscreteUsers([]);
    } finally {
      setLoadingDiscreteUsers(false);
    }
  };

  // Add function to fetch NDA register data
  const fetchNdaRegister = async () => {
    try {
      setLoadingNdaRegister(true);
      
      const data = await apiService.get(`/ndaregister/list/${id}`);
      setNdaRegister(data);
      console.log('NDA register loaded:', data);
      
    } catch (err) {
      console.error('Error fetching NDA register:', err);
      setNdaRegister([]);
    } finally {
      setLoadingNdaRegister(false);
    }
  };

  // Add function to fetch administration access users
  const fetchAdminUsers = async () => {
    try {
      setLoadingAdminUsers(true);
      
      const data = await apiService.get(`/properties/${id}/property-admin-users`);
      setAdminUsers(data);
      console.log('Administration users loaded:', data);
      
    } catch (err) {
      console.error('Error fetching administration users:', err);
      setAdminUsers([]);
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  // Add download function for NDA files
  const handleNdaDownload = async (item) => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/ndaregister/download/${item.id}`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the filename from the response headers or use the ndaFileName
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = item.ndaFileName;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create a blob from the response
      const blob = await response.blob();
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading NDA:', error);
      alert('Failed to download NDA file');
    }
  };

  // Add function to handle approve/reject NDA
  const handleNdaApproveReject = async (item, round, status) => {
    try {
      const requestBody = {
        adminUserId: 4,
        round: round,
        status: status
      };

      console.log('Submitting NDA approval/rejection:', requestBody);

      await apiService.put(`/ndaregister/${item.id}/approve`, requestBody);
      
      alert(`NDA ${round} ${status} successfully!`);
      
      // Refresh the NDA register list
      fetchNdaRegister();
      
    } catch (err) {
      console.error('Error submitting NDA approval/rejection:', err);
      alert(`Failed to ${status.toLowerCase()} NDA ${round}. Please try again.`);
    }
  };

  // Administration Access functions
  const searchAdminUsers = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setAdminSearchResults([]);
      return;
    }

    try {
      setAdminSearchLoading(true);
      
      const data = await apiService.get(`/users/adminusers?searchCriteria=${encodeURIComponent(searchTerm)}`);
      // Limit to maximum 20 results
      setAdminSearchResults(data.slice(0, 20));
      
    } catch (err) {
      console.error('Error searching administration users:', err);
      setAdminSearchResults([]);
    } finally {
      setAdminSearchLoading(false);
    }
  };

  const handleAdminUserSearchChange = (value) => {
    setAdminUserSearch(value);
    setSelectedAdminUser(null); // Clear selection when typing
    
    // Clear existing timer
    if (adminDebounceTimer.current) {
      clearTimeout(adminDebounceTimer.current);
    }
    
    // Set new timer for debounced search
    adminDebounceTimer.current = setTimeout(() => {
      searchAdminUsers(value);
    }, 500); // 500ms delay after user stops typing
  };

  const handleAdminUserSelect = (user) => {
    setSelectedAdminUser(user);
    setAdminUserSearch(`${user.firstName} ${user.lastName} (${user.emailAddress})`);
    setAdminSearchResults([]);
  };

  const handleSubmitAdminUser = async () => {
    try {
      setSubmittingAdminUser(true);

      if (!selectedAdminUser) {
        alert('Please select a user');
        return;
      }

      console.log('Submitting administration user:', {
        propertyId: id,
        userId: selectedAdminUser.id
      });

      await apiService.post(`/properties/property-admin-users`, {
        propertyId: id,
        userId: selectedAdminUser.id
      });
      
      alert('Administration access granted successfully!');
      
      // Reset form
      setSelectedAdminUser(null);
      setAdminUserSearch('');
      fetchAdminUsers(); // refresh list
      
    } catch (err) {
      console.error('Error submitting administration user:', err);
      alert('Failed to grant administration access. Please try again.');
    } finally {
      setSubmittingAdminUser(false);
    }
  };

  return (
    <div className="vault-tab">
      {/* Two Column Layout */}
      <div className="vault-content" style={{ display: 'flex', gap: '20px' }}>
        {/* Column 1 */}
        <div className="vault-left-column" style={{ flex: '0 0 50%' }}>
          
          {/* Section 1: NDA Link */}
          <div className="vault-section">
            <div className="form-section">
              <h3>NDA Link</h3>
              <div className="form-row">
                <label htmlFor="ndaSignatureLink">NDA Signature Link:</label>
                <input
                  type="url"
                  id="ndaSignatureLink"
                  className="form-input"
                  value={vaultData.ndaSignatureLink}
                  onChange={(e) => handleInputChange('ndaSignatureLink', e.target.value)}
                  placeholder="https://..."
                  autoComplete="off"
                />
              </div>
              <div className="form-actions" style={{ marginTop: '15px' }}>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleSubmitNDALink}
                  disabled={saving}
                >
                  {saving ? 'Submitting...' : 'Submit'}
                </button>
              </div>
              
              {/* Display current signed document URL if it exists */}
              {propertyData?.signedDocUrl && (
                <div className="form-row" style={{ marginTop: '15px' }}>
                  <label>Current NDA Link:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    wordBreak: 'break-all'
                  }}>
                    <a 
                      href={propertyData.signedDocUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: 'white', textDecoration: 'underline' }}
                    >
                      {propertyData.signedDocUrl}
                    </a>
                  </div>
                </div>
              )}

              {/* Display NDA Doc URL if it exists */}
              {propertyData?.ndaDocUrl && (
                <div className="form-row" style={{ marginTop: '15px' }}>
                  <label>NDA Document URL:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    wordBreak: 'break-all'
                  }}>
                    <a 
                      href={propertyData.ndaDocUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: 'white', textDecoration: 'underline' }}
                    >
                      {propertyData.ndaDocUrl}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Dataroom Checklist */}
          <div className="vault-section" style={{ marginTop: '30px' }}>
            <div className="form-section">
              <h3>Dataroom Checklist</h3>
              
              {/* Check if propertyData has a checklist value */}
              {propertyData?.checklist ? (
                // Display existing checklist value
                <div className="form-row">
                  <label>Current Checklist Type:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    {propertyData.checklist}
                  </div>
                </div>
              ) : (
                // Show dropdown for new selection
                <>
                  <div className="form-row">
                    <label htmlFor="dataroomChecklist">Checklist Type:</label>
                    <select
                      id="dataroomChecklist"
                      className="form-select"
                      value={vaultData.dataroomChecklist}
                      onChange={(e) => handleInputChange('dataroomChecklist', e.target.value)}
                      autoComplete="off"
                    >
                      <option value="">Select checklist type</option>
                      <option value="Office">Office</option>
                      <option value="Hospitality">Hospitality</option>
                      <option value="Filling station">Filling station</option>  
                      <option value="Hospital">Hospital</option>       
                      <option value="Residential">Residential</option>  
                      <option value="Retail">Retail</option>   
                      <option value="Leisure">Leisure</option>                       
                                                                                                       
                    </select>
                  </div>
                  <div className="form-actions" style={{ marginTop: '15px' }}>
                    <button 
                      type="button" 
                      className="btn-primary"
                      onClick={handleSubmitChecklist}
                      disabled={savingChecklist}
                    >
                      {savingChecklist ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 3: Custom NDA */}
          <div className="vault-section" style={{ marginTop: '30px' }}>
            <div className="form-section">
              <h3>Custom NDA</h3>
              
              {/* User Search */}
              <div className="form-row">
                <label htmlFor="userSearch">Search User:</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="userSearch"
                    className="form-input"
                    value={userSearch}
                    onChange={(e) => handleUserSearchChange(e.target.value)}
                    placeholder="Type at least 3 characters to search..."
                    style={{ paddingRight: '35px' }} // Add space for the icon
                    autoComplete="off"
                  />
                  
                  {/* Clear/Redo Icon */}
                  {(selectedUser || userSearch) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUser(null);
                        setUserSearch('');
                        setSearchResults([]);
                        // Focus the input field
                        setTimeout(() => {
                          document.getElementById('userSearch').focus();
                        }, 0);
                      }}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--cbre-text-secondary)',
                        fontSize: '16px',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--cbre-bg-secondary)';
                        e.target.style.color = 'var(--cbre-text)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--cbre-text-secondary)';
                      }}
                      title="Clear search and start over"
                    >
                      ↻
                    </button>
                  )}
                  
                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && !selectedUser && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'var(--cbre-bg-primary)',
                        border: '1px solid var(--cbre-border)',
                        borderTop: 'none',
                        borderRadius: '0 0 4px 4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}
                    >
                      {searchResults.map((user) => (
                        <div
                          key={user.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleUserSelect(user);
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--cbre-border)',
                            backgroundColor: 'var(--cbre-bg-primary)',
                            color: 'var(--cbre-text)',
                            userSelect: 'none',
                            pointerEvents: 'auto'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--cbre-bg-secondary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--cbre-bg-primary)';
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>
                            {user.firstName} {user.lastName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                            {user.emailAddress}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Loading indicator */}
                  {searchLoading && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      padding: '10px',
                      backgroundColor: 'var(--cbre-bg-primary)',
                      border: '1px solid var(--cbre-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      textAlign: 'center',
                      color: 'var(--cbre-text-secondary)'
                    }}>
                      Searching...
                    </div>
                  )}
                </div>
              </div>

              {/* Selected User Display */}
              {selectedUser && (
                <div className="form-row" style={{ marginTop: '15px' }}>
                  <label>Selected User:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                      {selectedUser.emailAddress}
                    </div>
                  </div>
                </div>
              )}

              {/* File Upload */}
              <div className="form-row" style={{ marginTop: '15px' }}>
                <label htmlFor="ndaFile">NDA File:</label>
                <input
                  type="file"
                  id="ndaFile"
                  className="form-input"
                  onChange={handleNDAFileChange}
                  accept=".pdf,.doc,.docx"
                  autoComplete="off"
                />
              </div>

              {/* Submit Button */}
              <div className="form-actions" style={{ marginTop: '15px' }}>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleSubmitCustomNDA}
                  disabled={uploadingNDA || !selectedUser || !ndaFile}
                >
                  {uploadingNDA ? 'Uploading...' : 'Submit Custom NDA'}
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* Column 2 */}
        <div className="vault-right-column" style={{ flex: '0 0 50%' }}>
          
          {/* Section 5: Discrete Users */}
          <div className="vault-section">
            <div className="form-section">
              <h3>Discrete Users</h3>

              
              {/* Results table from API (uses response keys as headers) */}
              {loadingDiscreteUsers ? (
                <div style={{ padding: '12px', textAlign: 'center' }}>Loading discrete users...</div>
              ) : discreteUsers.length > 0 ? (
                <div className="media-table-container" style={{ marginBottom: '16px' }}>
                  <table className="media-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        {Object.keys(discreteUsers[0])
                          .filter(key => !['id', 'propertyid', 'propertyId', 'isDeleted', 'username', 'userId'].includes(key))
                          .map((key) => (
                            <th key={key}>
                              {key}
                            </th>
                          ))}
                        <th style={{ width: '60px', textAlign: 'center' }}>Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discreteUsers.map((row, idx) => (
                        <tr key={row.id || idx}>
                          <td>{row.username || ''}</td>
                          {Object.keys(discreteUsers[0])
                            .filter(key => !['id', 'propertyid', 'propertyId', 'isDeleted', 'username', 'userId'].includes(key))
                            .map((key) => (
                              <td key={key}>
                                {String(row?.[key] ?? '')}
                              </td>
                            ))}
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!confirm('Are you sure you want to remove this user from discrete access?')) {
                                  return;
                                }
                                
                                try {
                                  await apiService.put(`/property-discrete-users/${row.id}/remove`, {
                                    userId: 4
                                  });
                                  
                                  // Refresh the discrete users list
                                  fetchDiscreteUsers();
                                  alert('User removed from discrete access successfully!');
                                } catch (error) {
                                  console.error('Error removing discrete user:', error);
                                  alert('Failed to remove user. Please try again.');
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                padding: '4px',
                                fontSize: '16px'
                              }}
                              title="Remove user from discrete access"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ marginBottom: '16px', color: 'var(--cbre-text-secondary)' }}>No discrete users found.</div>
              )}
          
              
              {/* User Search */}
              <div className="form-row">
                <label htmlFor="discreteUserSearch">Search User:</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="discreteUserSearch"
                    className="form-input"
                    value={discreteUserSearch}
                    onChange={(e) => handleDiscreteUserSearchChange(e.target.value)}
                    placeholder="Type at least 3 characters to search..."
                    style={{ paddingRight: '35px' }} // Add space for the icon
                    autoComplete="off"
                  />
                  
                  {/* Clear/Redo Icon */}
                  {(selectedDiscreteUser || discreteUserSearch) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDiscreteUser(null);
                        setDiscreteUserSearch('');
                        setDiscreteSearchResults([]);
                        // Focus the input field
                        setTimeout(() => {
                          document.getElementById('discreteUserSearch').focus();
                        }, 0);
                      }}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--cbre-text-secondary)',
                        fontSize: '16px',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--cbre-bg-secondary)';
                        e.target.style.color = 'var(--cbre-text)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--cbre-text-secondary)';
                      }}
                      title="Clear search and start over"
                    >
                      ↻
                    </button>
                  )}
                  
                  {/* Search Results Dropdown */}
                  {discreteSearchResults.length > 0 && !selectedDiscreteUser && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'var(--cbre-bg-primary)',
                      border: '1px solid var(--cbre-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {discreteSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleDiscreteUserSelect(user);
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--cbre-border)',
                            backgroundColor: 'var(--cbre-bg-primary)',
                            color: 'var(--cbre-text)',
                            userSelect: 'none',
                            pointerEvents: 'auto'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--cbre-bg-secondary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--cbre-bg-primary)';
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>
                            {user.firstName} {user.lastName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                            {user.emailAddress}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Loading indicator */}
                  {discreteSearchLoading && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      padding: '10px',
                      backgroundColor: 'var(--cbre-bg-primary)',
                      border: '1px solid var(--cbre-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      textAlign: 'center',
                      color: 'var(--cbre-text-secondary)'
                    }}>
                      Searching...
                    </div>
                  )}
                </div>
              </div>

              {/* Selected User Display */}
              {selectedDiscreteUser && (
                <div className="form-row" style={{ marginTop: '15px' }}>
                  <label>Selected User:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedDiscreteUser.firstName} {selectedDiscreteUser.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                      {selectedDiscreteUser.emailAddress}
                    </div>
                  </div>
                </div>
              )}

              {/* Conditional Submit Button or Warning Message */}
              <div className="form-actions" style={{ marginTop: '15px' }}>

                
                {propertyData?.isActive === true && propertyData?.isDiscrete === true ? (
                  <button 
                    type="button" 
                    className="btn-primary"
                    onClick={handleSubmitDiscreteUser}
                    disabled={submittingDiscreteUser || !selectedDiscreteUser}
                  >
                    {submittingDiscreteUser ? 'Granting Access...' : 'Grant Discrete Access'}
                  </button>
                ) : (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    color: '#856404',
                    fontSize: '14px'
                  }}>
                    The property needs to be marked active and discrete before you can add users

                  </div>
                )}
              </div>

            </div>
          </div>

          {/* Section 6: Administration Access */}
          <div className="vault-section" style={{ marginTop: '30px' }}>
            <div className="form-section">
              <h3>Administration Access</h3>
              
              {/* Results table from API */}
              {loadingAdminUsers ? (
                <div style={{ padding: '12px', textAlign: 'center' }}>Loading administration users...</div>
              ) : adminUsers.length > 0 ? (
                <div className="media-table-container" style={{ marginBottom: '16px' }}>
                  <table className="media-table">
                    <thead>
                      <tr>
                        <th>Administrator</th>
                        <th>Role</th>
                        <th>Added Date</th>
                        <th>Added By</th>
                        <th style={{ width: '60px', textAlign: 'center' }}>Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div style={{ fontWeight: 'bold' }}>{item.adminName || ''}</div>
                            {item.user_email && (
                              <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                                {item.user_email}
                              </div>
                            )}
                          </td>
                          <td>{item.role || ''}</td>
                          <td>{item.addeddate ? new Date(item.addeddate).toLocaleDateString() : ''}</td>
                          <td>{item.addedByname || ''}</td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              type="button"
                              onClick={async () => {
                                if (!confirm('Are you sure you want to remove this user from administration access?')) {
                                  return;
                                }
                                
                                try {
                                  await apiService.delete(`/properties/property-admin-users/${item.id}`);
                                  
                                  // Refresh the administration users list
                                  fetchAdminUsers();
                                  alert('User removed from administration access successfully!');
                                } catch (error) {
                                  console.error('Error removing administration user:', error);
                                  alert('Failed to remove user. Please try again.');
                                }
                              }}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                padding: '4px',
                                fontSize: '16px'
                              }}
                              title="Remove user from administration access"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ marginBottom: '16px', color: 'var(--cbre-text-secondary)' }}>No administration users found.</div>
              )}
          
              {/* User Search */}
              <div className="form-row">
                <label htmlFor="adminUserSearch">Search User:</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="adminUserSearch"
                    className="form-input"
                    value={adminUserSearch}
                    onChange={(e) => handleAdminUserSearchChange(e.target.value)}
                    placeholder="Type at least 3 characters to search..."
                    style={{ paddingRight: '35px' }}
                    autoComplete="off"
                  />
                  
                  {/* Clear/Redo Icon */}
                  {(selectedAdminUser || adminUserSearch) && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedAdminUser(null);
                        setAdminUserSearch('');
                        setAdminSearchResults([]);
                        setTimeout(() => {
                          document.getElementById('adminUserSearch').focus();
                        }, 0);
                      }}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--cbre-text-secondary)',
                        fontSize: '16px',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = 'var(--cbre-bg-secondary)';
                        e.target.style.color = 'var(--cbre-text)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = 'var(--cbre-text-secondary)';
                      }}
                      title="Clear search and start over"
                    >
                      ↻
                    </button>
                  )}
                  
                  {/* Search Results Dropdown */}
                  {adminSearchResults.length > 0 && !selectedAdminUser && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'var(--cbre-bg-primary)',
                      border: '1px solid var(--cbre-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {adminSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAdminUserSelect(user);
                          }}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--cbre-border)',
                            backgroundColor: 'var(--cbre-bg-primary)',
                            color: 'var(--cbre-text)',
                            userSelect: 'none',
                            pointerEvents: 'auto'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--cbre-bg-secondary)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--cbre-bg-primary)';
                          }}
                        >
                          <div style={{ fontWeight: 'bold' }}>
                            {user.firstName} {user.lastName}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                            {user.emailAddress}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Loading indicator */}
                  {adminSearchLoading && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      padding: '10px',
                      backgroundColor: 'var(--cbre-bg-primary)',
                      border: '1px solid var(--cbre-border)',
                      borderTop: 'none',
                      borderRadius: '0 0 4px 4px',
                      textAlign: 'center',
                      color: 'var(--cbre-text-secondary)'
                    }}>
                      Searching...
                    </div>
                  )}
                </div>
              </div>

              {/* Selected User Display */}
              {selectedAdminUser && (
                <div className="form-row" style={{ marginTop: '15px' }}>
                  <label>Selected User:</label>
                  <div style={{
                    padding: '10px',
                    backgroundColor: 'var(--cbre-bg-secondary)',
                    border: '1px solid var(--cbre-border)',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedAdminUser.firstName} {selectedAdminUser.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--cbre-text-secondary)' }}>
                      {selectedAdminUser.emailAddress}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="form-actions" style={{ marginTop: '15px' }}>
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleSubmitAdminUser}
                  disabled={submittingAdminUser || !selectedAdminUser}
                >
                  {submittingAdminUser ? 'Granting Access...' : 'Grant Administration Access'}
                </button>
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* Full Width Section Below Columns */}
      <div style={{ marginTop: '30px' }}>
        {/* Section 4: NDA Signatories */}
        <div className="vault-section">
          <div className="form-section">
            <h3>NDA Signatories</h3>
            
            {loadingNdaRegister ? (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--cbre-text-secondary)' }}>
                Loading NDA signatories...
              </div>
            ) : ndaRegister.length > 0 ? (
              <div className="media-table-container">
                <table className="media-table">
                  <thead>
                    <tr>
                      <th>NDA File</th>
                      <th>User</th>
                      <th>Added By</th>
                      <th>Round 1 Status</th>
                      <th>Round 2 Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ndaRegister.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <button
                            onClick={() => handleNdaDownload(item)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'var(--cbre-text-primary)',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0,
                              fontSize: 'inherit'
                            }}
                          >
                            {item.ndaFileName}
                          </button>
                        </td>
                        <td>{item.user.firstName} {item.user.lastName}</td>
                        <td>{item.addedBy.firstName} {item.addedBy.lastName}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>
                              {item.round1AcceptYN === true ? 'Accepted' : 
                               item.round1AcceptYN === false ? 'Rejected' : 'Pending'}
                            </span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to ACCEPT Round 1?')) {
                                    handleNdaApproveReject(item, 'round1', 'Accepted');
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'green',
                                  fontSize: '16px',
                                  padding: '2px'
                                }}
                                title="Accept Round 1"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to REJECT Round 1?')) {
                                    handleNdaApproveReject(item, 'round1', 'Rejected');
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'red',
                                  fontSize: '16px',
                                  padding: '2px'
                                }}
                                title="Reject Round 1"
                              >
                                ✗
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>
                              {item.round2AcceptYN === true ? 'Accepted' : 
                               item.round2AcceptYN === false ? 'Rejected' : 'Pending'}
                            </span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to ACCEPT Round 2?')) {
                                    handleNdaApproveReject(item, 'round2', 'Accepted');
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'green',
                                  fontSize: '16px',
                                  padding: '2px'
                                }}
                                title="Accept Round 2"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to REJECT Round 2?')) {
                                    handleNdaApproveReject(item, 'round2', 'Rejected');
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: 'red',
                                  fontSize: '16px',
                                  padding: '2px'
                                }}
                                title="Reject Round 2"
                              >
                                ✗
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>{new Date(item.dateOn).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--cbre-bg-secondary)', 
                border: '1px solid var(--cbre-border)', 
                borderRadius: '4px',
                minHeight: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--cbre-text-secondary)'
              }}>
                No NDA signatories found for this property.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VaultTab;